export interface FooterCell {
  label: string;
  value?: string | number;
  rowSpan?: number;
  colSpan?: number;
  type?: "normal" | "highlight" | "error";
  backgroundColor?: string;
  isLabelOnly?: boolean; // 라벨만 표시할지 여부
}

export interface FooterGridProps {
  grid: (FooterCell | null)[][];
  className?: string;
}

export interface FooterItem {
  label: string;
  value: string | number;
  type?: "normal" | "highlight" | "error";
  backgroundColor?: string;
}

export interface FooterSection {
  items: FooterItem[];
  backgroundColor?: string;
}

export interface FooterProps {
  sections?: FooterSection[];
  grid?: (FooterCell | null)[][];
  className?: string;
}

// 그리드 기반 Footer 컴포넌트
export function FooterGrid({ grid, className = "" }: FooterGridProps) {
  if (!grid || grid.length === 0) return null;

  // 각 행의 첫/마지막 실제 셀 인덱스
  const firstIdx = grid.map(row => row.findIndex(c => !!c));
  const lastIdx = grid.map(row => {
    for (let i = row.length - 1; i >= 0; i--) if (row[i]) return i;
    return -1;
  });

  const topRow = 0;
  const bottomRow = grid.length - 1;
  const topLeftIdx = firstIdx[topRow];
  const topRightIdx = lastIdx[topRow];
  const bottomLeftIdx = firstIdx[bottomRow];
  const bottomRightIdx = lastIdx[bottomRow];

  const formatValue = (value: string | number) => {
    if (typeof value === "number") return value.toLocaleString();
    if (typeof value === "string" && !isNaN(Number(value))) return Number(value).toLocaleString();
    return value;
  };

  return (
    <div className={`bg-white ${className}`}>
      {/* 바깥 테두리/라운드는 래퍼가 담당 */}
      <div className="rounded-[4px] overflow-hidden">
        <table className="w-full border-collapse">
          <tbody>
            {grid.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => {
                  if (!cell) return null;

                  // 라벨 전용 셀 여부 판별 (값이 없거나 isLabelOnly=true 인 경우)
                  const isLabelOnly =
                    cell.isLabelOnly || cell.value === undefined || cell.value === null || cell.value === "";

                  // 코너 판별(바깥 4모서리만 둥글게)
                  const isTopLeft = rowIndex === topRow && cellIndex === topLeftIdx;
                  const isTopRight = rowIndex === topRow && cellIndex === topRightIdx;
                  const isBottomLeft = rowIndex === bottomRow && cellIndex === bottomLeftIdx;
                  const isBottomRight = rowIndex === bottomRow && cellIndex === bottomRightIdx;

                  // 다음 보이는 셀 인덱스 & 라벨 여부
                  const nextIdx = (() => {
                    for (let j = cellIndex + 1; j < row.length; j++) {
                      if (row[j]) return j;
                    }
                    return -1;
                  })();

                  // 공통 색
                  const BG = cell.backgroundColor || "#BEDE8D";
                  const LINE = "#93C841";

                  // 행/열 끝 판단
                  const isLastCol = cellIndex === lastIdx[rowIndex];
                  const isFirstCol = cellIndex === firstIdx[rowIndex];

                  // rowSpan 고려한 아래 보더
                  const span = cell.rowSpan ?? 1;
                  const endsAt = rowIndex + span - 1;
                  const drawBottom = endsAt !== bottomRow;

                  return (
                    <td key={cellIndex} rowSpan={cell.rowSpan || 1} colSpan={cell.colSpan || 1} className="h-8 p-0">
                      {isLabelOnly ? (
                        // [라벨-only 셀] 값 없이 라벨만 표시 → 배경색/내부 보더 스타일 처리
                        <div
                          className="flex items-center justify-center h-full px-4 text-base font-medium text-[#333]"
                          style={{
                            backgroundColor: BG,
                            borderStyle: "solid",
                            borderWidth: 1,
                            // 바깥쪽(위/왼)은 항상 배경색 → 보더가 없는 것처럼 보임
                            borderTopColor: rowIndex === topRow ? BG : BG,
                            borderLeftColor: isFirstCol ? BG : BG,
                            // 내부 가로선: drawBottom일 때만 초록, 마지막이면 배경색
                            borderBottomColor: drawBottom ? LINE : BG,
                            borderRightColor: LINE,
                            // 둥근 모서리 유지
                            borderTopLeftRadius: isTopLeft ? 4 : 0,
                            borderBottomLeftRadius: isBottomLeft ? 4 : 0,
                            borderTopRightRadius: isTopRight ? 4 : 0,
                            borderBottomRightRadius: isBottomRight ? 4 : 0,
                          }}
                        >
                          {cell.label}
                        </div>
                      ) : (
                        // [라벨+값 셀] 라벨과 값 영역을 flex로 분리하여 각각 스타일 적용
                        <div className="flex h-full">
                          {/* 라벨 */}
                          <div
                            className={[
                              "flex items-center justify-center flex-1 px-5 py-2 whitespace-nowrap text-base font-medium text-[#333]",
                              "border-r border-[#93C841]",
                              drawBottom && "border-b border-[#93C841]",
                              isTopLeft && "rounded-tl-[4px]",
                              isBottomLeft && "rounded-bl-[4px]",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            style={{ backgroundColor: "#BEDE8D" }}
                          >
                            {cell.label}
                          </div>

                          {/* 값 */}
                          <div
                            className={[
                              "flex items-center justify-end flex-[3] bg-white pr-[22px] text-base font-semibold text-[#FF3232]",
                              // 맨 윗행만 위선(겹침 방지)
                              rowIndex === topRow && "border-t border-[#93C841]",
                              // 내부선 또는 '맨 마지막 행' 외곽선(아래) — 값 칸에만 적용
                              (drawBottom || rowIndex === bottomRow) && "border-b border-[#93C841]",
                              // 맨 오른쪽 외곽선
                              isLastCol && "border-r border-[#93C841]",
                              isTopRight && "rounded-tr-[4px]",
                              isBottomRight && "rounded-br-[4px]",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            {formatValue(cell.value!)}
                          </div>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Footer({ sections = [], grid, className = "" }: FooterProps) {
  // 그리드가 제공되면 그리드 컴포넌트 사용
  if (grid) {
    return <FooterGrid grid={grid} className={className} />;
  }

  if (sections.length === 0) {
    return null;
  }

  const formatValue = (value: string | number) => {
    if (typeof value === "number") {
      return value.toLocaleString();
    }
    if (typeof value === "string" && !isNaN(Number(value))) {
      return Number(value).toLocaleString();
    }
    return value;
  };

  return (
    <div className={`bg-white ${className}`}>
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} className="flex items-stretch w-full">
          {section.items.map((item, itemIndex) => {
            const isFirst = itemIndex === 0;
            const isLast = itemIndex === section.items.length - 1;

            return (
              <div key={itemIndex} className="flex-1 flex items-stretch last:border-r-0">
                {/* Label 부분 - 1/4 비율 */}
                <div
                  className={`flex items-center justify-center h-12 flex-1 px-5 py-2 whitespace-nowrap text-base font-medium text-[#333] ${
                    isFirst ? "rounded-l-[4px]" : ""
                  }`}
                  style={{
                    backgroundColor: "#BEDE8D",
                  }}
                >
                  <span>{item.label}</span>
                </div>
                {/* Value 부분 - 3/4 비율 */}
                <div
                  className={`flex items-center justify-end h-12 bg-white pr-[22px] text-base font-semibold text-[#FF3232] border border-[#BEDE8D] ${
                    isLast ? "rounded-r-[4px]" : ""
                  }`}
                  style={{
                    flex: "3",
                  }}
                >
                  <span>{formatValue(item.value)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
